
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "motion/react";
import {
  TrendingUp,
  Activity,
  BarChart3,
  Eye,
  LineChart,
  Bell,
  Building2,
  Filter,
} from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";

const translations = {
  en: {
    title: "Platform Features",
    subtitle: "One platform, countless ways to read the market",
    comingNext: "What's Coming Next",
    comingNextDesc: "We are continuously expanding. Here are the exciting tools arriving soon!",
    soon: "Soon",
    features: [
      {
        icon: Eye,
        title: "Watchlist",
        description: "Keep a close eye on your favorite stocks. Track live movements, momentum, and key levels seamlessly in one unified dashboard.",
      },
      {
        icon: BarChart3,
        title: "Market Analysis",
        description: "Understand big player movements and money flow with clear Accumulation-Distribution narratives and broad market overviews.",
      },
      {
        icon: Activity,
        title: "Broker Analysis",
        description: "Monitor broker activity granularly through Broker Summary, Transaction, and Inventory to spot accumulation trends and behavioral shifts.",
      },
      {
        icon: TrendingUp,
        title: "Stock Analysis",
        description: "Uncover hidden opportunities by diving deep into stock transaction patterns with Trade Footprints and Trade Details, then feel the power of Bid-Offer Replay to crack the code before a stock takes off.",
      },
    ],
    comingFeatures: [
      {
        icon: LineChart,
        title: "Trend Tracking",
        description: "Automatically track and identify emerging market trends to capture massive opportunities early in the cycle.",
      },
      {
        icon: Building2,
        title: "Emiten Info",
        description: "Access comprehensive fundamental data and corporate actions to support your qualitative analysis.",
      },
      {
        icon: Filter,
        title: "Technical Screener",
        description: "Filter thousands of stocks instantly based on your specific technical criteria and trading strategy.",
      },
      {
        icon: Bell,
        title: "Real Time Alert",
        description: "Never miss a trade with custom notifications triggered by price action, volume spikes, and indicator crossovers.",
      },
    ]
  },
  id: {
    title: "Platform Features",
    subtitle: "One platform, countless ways to read the market",
    comingNext: "What's Coming Next",
    comingNextDesc: "We are continuously expanding. Here are the exciting tools arriving soon!",
    soon: "Segera",
    features: [
      {
        icon: Eye,
        title: "Watchlist",
        description: "Pantau pergerakan saham favorit Anda. Lacak pergerakan langsung, momentum, dan level kunci secara mulus dalam satu dasbor terpadu.",
      },
      {
        icon: BarChart3,
        title: "Market Analysis",
        description: "Pahami pergerakan pemain besar dan aliran dana dengan narasi Akumulasi-Distribusi yang jelas dan gambaran pasar yang luas.",
      },
      {
        icon: Activity,
        title: "Broker Analysis",
        description: "Pantau aktivitas broker secara terperinci melalui Ringkasan Broker, Transaksi, dan Inventaris untuk menemukan tren akumulasi dan pergeseran perilaku.",
      },
      {
        icon: TrendingUp,
        title: "Stock Analysis",
        description: "Temukan peluang tersembunyi dengan menyelami pola transaksi saham melalui Trade Footprints dan Trade Details, lalu rasakan kekuatan Bid-Offer Replay sebelum harga melonjak.",
      },
    ],
    comingFeatures: [
      {
        icon: LineChart,
        title: "Trend Tracking",
        description: "Lacak dan identifikasi tren pasar yang muncul secara otomatis untuk menangkap peluang besar lebih awal.",
      },
      {
        icon: Building2,
        title: "Emiten Info",
        description: "Akses data fundamental komprehensif dan aksi korporasi untuk mendukung analisis kualitatif Anda.",
      },
      {
        icon: Filter,
        title: "Technical Screener",
        description: "Saring ribuan saham secara instan berdasarkan kriteria teknikal spesifik dan strategi perdagangan Anda.",
      },
      {
        icon: Bell,
        title: "Real Time Alert",
        description: "Jangan pernah lewatkan peluang dengan notifikasi kustom yang dipicu oleh aksi harga, lonjakan volume, dan persilangan indikator.",
      },
    ]
  }
};

export function FeaturesPage() {
  const [lang, setLang] = useState<'id' | 'en'>('id');
  const t = translations[lang];

  return (
    <div className="relative h-screen flex flex-col bg-gradient-to-br from-background via-muted/20 to-background text-foreground overflow-hidden">
      <Helmet>
        <title>BandarmoloNY - Features & Platform Capabilities</title>
        <meta
          name="description"
          content="BandarmoloNY offers comprehensive trading analysis tools including Market Rotation (RRG, RRC, Seasonality), Broker Activity Intelligence, Technical Analysis with TradingView integration, Astrology insights, and advanced stock transaction tracking for Indonesian market."
        />
        <meta name="keywords" content="trading analysis, market rotation, broker activity, technical analysis, indonesian stock market, RRG, RRC, seasonality, trading tools" />
        <link rel="canonical" href="https://bandarmolony.com/features" />
        <meta property="og:title" content="BandarmoloNY - Features & Trading Platform Capabilities" />
        <meta property="og:description" content="Discover comprehensive trading analysis tools for Indonesian stock market including Market Rotation, Broker Intelligence, Technical Analysis, and more." />
        <meta property="og:url" content="https://bandarmolony.com/features" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="BandarmoloNY - Features & Trading Platform" />
        <meta name="twitter:description" content="Advanced trading analysis tools for Indonesian stock market." />
      </Helmet>



      <div className="flex-shrink-0">
        <Navbar />
      </div>

      <main className="relative z-10 mx-auto flex flex-1 w-full max-w-7xl flex-col gap-4 px-4 pt-16 pb-2 md:px-6 md:pt-20 overflow-y-auto min-h-0">
        <div className="relative mt-2 md:mt-4">
          <div className="absolute right-0 top-0 flex bg-background/50 border border-white/10 rounded-lg p-1 backdrop-blur-sm z-20">
            <button 
              onClick={() => setLang('id')} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${lang === 'id' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              ID
            </button>
            <button 
              onClick={() => setLang('en')} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${lang === 'en' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              EN
            </button>
          </div>

          <motion.header
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-xl font-bold tracking-tight md:text-2xl">{t.title}</h1>
            <p className="mx-auto mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground md:text-sm px-16">
              {t.subtitle}
            </p>
          </motion.header>
        </div>

        <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mt-2">
          {t.features.map((feature, index) => (
            <motion.article
              key={feature.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="flex flex-col rounded-2xl border border-white/10 bg-background/80 px-4 py-3 shadow-lg backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_35px_rgba(239,68,68,0.4)] dark:border-white/5"
            >
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <feature.icon className="h-4 w-4" aria-hidden="true" />
                </div>
                <h2 className="text-base font-semibold text-foreground md:text-lg">{feature.title}</h2>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-foreground/80 md:text-sm">
                {feature.description}
              </p>
            </motion.article>
          ))}
        </section>

        {/* What's Coming Next Section */}
        <motion.header
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="text-center mt-2"
        >
          <h2 className="text-xl font-bold tracking-tight md:text-2xl text-primary">{t.comingNext}</h2>
          <p className="mx-auto mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground md:text-sm">
            {t.comingNextDesc}
          </p>
        </motion.header>

        <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 pb-8">
          {t.comingFeatures.map((feature, index) => (
            <motion.article
              key={feature.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group flex flex-col rounded-2xl border border-white/10 bg-muted/30 px-4 py-3 shadow-lg backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:bg-muted/50 dark:border-white/5"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground/5 text-muted-foreground group-hover:text-primary transition-colors">
                    <feature.icon className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground opacity-75 group-hover:opacity-100 transition-opacity md:text-lg">{feature.title}</h3>
                </div>
                <span className="text-[9px] uppercase font-bold tracking-widest text-primary/70 bg-primary/10 px-2 py-0.5 rounded-full">{t.soon}</span>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-foreground opacity-60 md:text-sm">
                {feature.description}
              </p>
            </motion.article>
          ))}
        </section>


      </main>

      {/* SEO Backlinks Hidden Section */}
      <div className="sr-only">
        <h2>Market Analysis Platform by Hints Technology</h2>
        <nav>
          <ul>
            <li><a href="https://www.hintstechnology.com/" rel="nofollow noopener noreferrer">Hints Technology - Software Development</a></li>
            <li><a href="https://bandarmolony.com/" rel="nofollow noopener noreferrer">BandarmoloNY Home</a></li>
            <li><a href="https://bandarmolony.com/pricing" rel="nofollow noopener noreferrer">BandarmoloNY Pricing</a></li>
            <li><a href="https://bandarmolony.com/contact" rel="nofollow noopener noreferrer">Contact BandarmoloNY</a></li>
          </ul>
        </nav>
        <p>Trading analysis platform developed by Hints Technology. Advanced market insights and data analytics for Indonesian stock market.</p>
      </div>

      <div className="flex-shrink-0">
        <Footer />
      </div>
    </div>
  );
}

